import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { push } from 'redux-first-history';

import FormContainer from '@starbucks-web/pattern-library/lib/components/form-container/';
import ContentColumn from '@starbucks-web/pattern-library/lib/components/content-column';
import { useModalContext } from '@starbucks-web/pattern-library/lib/components/modal-provider';

import H1 from 'shared/app/components/h1';
import SvcImage from 'shared/app/components/svc-image';
import FormWithMessaging from 'shared/app/components/form-with-messaging';

import { profileDataSelector, signedInSelector } from 'shared/app/bundles/user';
import { SessionCheckContext } from 'shared/app/shell/lib/session-check-provider';

import GiftPageHelmet from '../page-helmet';
import GiftBreadcrumbs from '../gift-breadcrumbs';
import PurchaseForm from '../purchase-form';
import UnAuthBottomSheet, {
  UNAUTH_GIFT_SHEET_TITLE_ID,
} from '../un-auth-bottom-sheet';
import TermsAndConditions from './terms-and-conditions';

import { updateGiftFormData } from '../../state/action-creators';
import {
  giftFormDataSelector,
  currentCardSelector,
  giftConfigSelector,
  numberOfRecipientsSelector,
} from '../../state/selectors';
import {
  setSessionGiftFormData,
  getSessionGiftFormData,
} from '../../utils/session-storage-gift';

import { getFormFields, MAX_MESSAGE_LENGTH } from '../../utils/gift-form';
import { setInitialRecipientStateWithSessionData } from '../../utils/recipients';
import messages from '../../messages';
import { svcMessages } from 'shared/app/messages/svc-cards';

// eslint-disable-next-line max-statements
const CardDetail = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { openModal } = useModalContext();
  const signedIn = useSelector(signedInSelector);
  const profileData = useSelector(profileDataSelector);
  const svcCard = useSelector(currentCardSelector);
  const giftFormData = useSelector(giftFormDataSelector);
  const { customAmountMax, customAmountMin, defaultGiftAmountOption } =
    useSelector(giftConfigSelector);
  const numberOfRecipients = useSelector(numberOfRecipientsSelector);

  const sessionGiftFormData = getSessionGiftFormData();

  const [isCustomFieldActive, setIsCustomFieldActive] = useState(
    sessionGiftFormData?.amount === 'custom'
  );
  const [recipients, setRecipients] = useState(
    setInitialRecipientStateWithSessionData(sessionGiftFormData)
  );

  if (!svcCard?.data || (signedIn && !profileData)) {
    return null;
  }

  const { data: svcCardData } = svcCard;

  const handleFieldsStateChange = (fieldsState, oldFieldsState) => {
    const amount = fieldsState?.fields?.amount?.input?.value ?? null;

    if (fieldsState) {
      setSessionGiftFormData(fieldsState.fields);
    }
    if (fieldsState === oldFieldsState) {
      return;
    }
    setIsCustomFieldActive(amount === 'custom');
  };

  const handleSubmit = (formData, checkReAuthRequired) => {
    const currentGiftFormData = {
      ...formData,
      productNumber: svcCard?.data?.productNumber,
    };

    dispatch(updateGiftFormData(currentGiftFormData));

    if (!signedIn) {
      return openModal({
        component: UnAuthBottomSheet,
        ariaLabelledBy: UNAUTH_GIFT_SHEET_TITLE_ID,
      });
    }

    // do not need a connected version of push, it will
    // be dispatched in the checkReAuthRequired handling
    checkReAuthRequired({
      nextAction: () => push('#payment'),
    });
  };

  const asset = svcCardData ? svcCardData.largeImageUrl : '';

  return (
    <SessionCheckContext.Consumer>
      {({ checkReAuthRequired }) => (
        <React.Fragment>
          <GiftPageHelmet cardName={svcCardData?.displayName || undefined} />
          <ContentColumn
            className="py5 lg-py9"
            data-e2e="giftCardDetail"
            size="md"
          >
            <GiftBreadcrumbs />
            <H1 className="text-bold mb4" size="lg">
              {formatMessage(messages.createEGift, { numberOfRecipients })}
            </H1>
            <SvcImage
              altText={`${formatMessage(svcMessages.giftAltContext)} ${
                svcCardData.altText
              }`}
              flexEmbedWrapperClassName="mb3"
              src={asset}
            />
            <FormContainer
              fields={getFormFields({
                giftFormData,
                defaultGiftAmountOption,
                customAmountMin,
                customAmountMax,
                updateGiftFormData,
                profileData,
                isCustomFieldActive,
                recipients,
              })}
              onFieldsStateChange={handleFieldsStateChange}
              onSubmit={(formData) =>
                handleSubmit(formData, checkReAuthRequired)
              }
            >
              <FormWithMessaging requiredIndicatorTextClassName="text-xs">
                <PurchaseForm
                  maxMessageLength={MAX_MESSAGE_LENGTH}
                  recipients={recipients}
                  setRecipients={setRecipients}
                />
              </FormWithMessaging>
            </FormContainer>
          </ContentColumn>
          <TermsAndConditions />
        </React.Fragment>
      )}
    </SessionCheckContext.Consumer>
  );
};

export default CardDetail;
